import React, { useMemo } from 'react';
import { FIELD_ZONES } from '../../../constants/rugbyConstants';
import RugbyFieldSVG from '../../common/RugbyFieldSVG';
import './HeatmapView.css';

const HeatmapView = ({ events, homeTeamName, awayTeamName, territorySplit }) => {
    // Group events by team for the heatmap
    const homeEvents = events.filter(event => event.team === 'Home');
    const awayEvents = events.filter(event => event.team === 'Away');
    
    // Generate heat map data
    const heatmapData = useMemo(() => {
        // Create a grid to represent event density
        const gridSize = 20;
        const grid = Array(gridSize).fill().map(() => Array(gridSize).fill(0));
        
        // Add events to grid
        events.forEach(event => {
            // Get the original position
            let x = event.position.x;
            
            // Adjust x position for second half due to direction change
            if (event.period === 'Second Half' || event.period === 'Overtime') {
                x = 1 - x; // Invert the x-coordinate
            }
            
            // Convert to grid coordinates
            const gridX = Math.floor(x * gridSize);
            const gridY = Math.floor(event.position.y * gridSize);
            
            // Ensure we're within bounds
            if (gridX >= 0 && gridX < gridSize && gridY >= 0 && gridY < gridSize) {
                grid[gridY][gridX] += 1;
            }
        });
        
        // Find maximum density for normalization
        let maxDensity = 0;
        for (let y = 0; y < gridSize; y++) {
            for (let x = 0; x < gridSize; x++) {
                maxDensity = Math.max(maxDensity, grid[y][x]);
            }
        }
        
        return { grid, maxDensity, gridSize };
    }, [events]);
    
    // Calculate zone widths for dynamic rendering
    const redZoneWidth = `${(FIELD_ZONES.RED_ZONE.end - FIELD_ZONES.RED_ZONE.start) * 100}%`;
    const yellowZoneWidth = `${(FIELD_ZONES.YELLOW_ZONE.end - FIELD_ZONES.YELLOW_ZONE.start) * 100}%`;
    const greenZoneWidth = `${(FIELD_ZONES.GREEN_ZONE.end - FIELD_ZONES.GREEN_ZONE.start) * 100}%`;
    
    return (
        <div className="stats-section">
            <h3 className="stats-section-title">Event Heatmap</h3>
            
            <div className="territory-stats">
                <div className="team-territory">
                    <div className="territory-title">{homeTeamName}</div>
                    <div className="territory-bar">
                        <div
                            className="territory-attacking"
                            style={{ width: `${territorySplit.home.attackingPercentage || 0}%` }}
                        >
                            {territorySplit.home.attackingPercentage || 0}%
                        </div>
                        <div
                            className="territory-defending"
                            style={{ width: `${territorySplit.home.defendingPercentage || 0}%` }}
                        >
                            {territorySplit.home.defendingPercentage || 0}%
                        </div>
                    </div>
                    <div className="territory-labels">
                        <span>Attacking</span>
                        <span>Defending</span>
                    </div>
                </div>

                <div className="team-territory">
                    <div className="territory-title">{awayTeamName}</div>
                    <div className="territory-bar">
                        <div
                            className="territory-attacking"
                            style={{ width: `${territorySplit.away.attackingPercentage || 0}%` }}
                        >
                            {territorySplit.away.attackingPercentage || 0}%
                        </div>
                        <div
                            className="territory-defending"
                            style={{ width: `${territorySplit.away.defendingPercentage || 0}%` }}
                        >
                            {territorySplit.away.defendingPercentage || 0}%
                        </div>
                    </div>
                    <div className="territory-labels">
                        <span>Attacking</span>
                        <span>Defending</span>
                    </div>
                </div>
            </div>
            
            <div className="heatmap-container">
                {/* External Legend */}
                <div className="field-legend">
                    <div className="legend-item">
                        <div className="legend-dot home-dot"></div>
                        <span>{homeTeamName}</span>
                    </div>
                    <div className="legend-item">
                        <div className="legend-dot away-dot"></div>
                        <span>{awayTeamName}</span>
                    </div>
                    <div className="legend-item">
                        <div className="density-indicator"></div>
                        <span>Density</span>
                    </div>
                </div>
                
                {/* Rugby Field Container */}
                <div className="rugby-field-container">
                    {/* Field SVG */}
                    <div className="field-svg-container">
                        <RugbyFieldSVG>
                            {/* Zone overlays using constants from FIELD_ZONES */}
                            <g>
                                {/* Red Zone */}
                                <rect
                                    x={FIELD_ZONES.RED_ZONE.start * 120}
                                    y="0"
                                    width={(FIELD_ZONES.RED_ZONE.end - FIELD_ZONES.RED_ZONE.start) * 120}
                                    height="70"
                                    className="zone-overlay-red"
                                />
                                
                                {/* Yellow Zone */}
                                <rect
                                    x={FIELD_ZONES.YELLOW_ZONE.start * 120}
                                    y="0"
                                    width={(FIELD_ZONES.YELLOW_ZONE.end - FIELD_ZONES.YELLOW_ZONE.start) * 120}
                                    height="70"
                                    className="zone-overlay-yellow"
                                />
                                
                                {/* Green Zone */}
                                <rect
                                    x={FIELD_ZONES.GREEN_ZONE.start * 120}
                                    y="0"
                                    width={(FIELD_ZONES.GREEN_ZONE.end - FIELD_ZONES.GREEN_ZONE.start) * 120}
                                    height="70"
                                    className="zone-overlay-green"
                                />
                            </g>
                            
                            {/* Heat map overlay */}
                            {heatmapData.maxDensity > 0 && heatmapData.grid.map((row, yIndex) => (
                                row.map((density, xIndex) => {
                                    if (density === 0) return null;
                                    
                                    // Calculate position and size
                                    const cellWidth = 120 / heatmapData.gridSize;
                                    const cellHeight = 70 / heatmapData.gridSize;
                                    const x = xIndex * cellWidth;
                                    const y = yIndex * cellHeight;
                                    
                                    // Calculate opacity based on density
                                    const opacity = Math.min(0.6, 0.1 + (density / heatmapData.maxDensity) * 0.5);
                                    
                                    return (
                                        <rect 
                                            key={`heatmap-${xIndex}-${yIndex}`}
                                            x={x}
                                            y={y}
                                            width={cellWidth}
                                            height={cellHeight}
                                            fill="#FF5722"
                                            opacity={opacity}
                                        />
                                    );
                                })
                            ))}
                            
                            {/* Event markers */}
                            {homeEvents.map((event, index) => {
                                // Calculate adjusted position for visualization
                                let adjustedX = event.position.x;
                                if (event.period === 'Second Half' || event.period === 'Overtime') {
                                    adjustedX = 1 - adjustedX; // Invert for second half/overtime
                                }
                                
                                return (
                                    <circle
                                        key={`home-${index}`}
                                        cx={adjustedX * 120}
                                        cy={event.position.y * 70}
                                        r="1.2"
                                        fill="var(--home-team-color, #604734)"
                                        stroke="white"
                                        strokeWidth="0.3"
                                        opacity="0.8"
                                    >
                                        <title>{`${event.eventType} - ${homeTeamName} - ${event.period}`}</title>
                                    </circle>
                                );
                            })}
                            
                            {awayEvents.map((event, index) => {
                                // Calculate adjusted position for visualization
                                let adjustedX = event.position.x;
                                if (event.period === 'Second Half' || event.period === 'Overtime') {
                                    adjustedX = 1 - adjustedX; // Invert for second half/overtime
                                }
                                
                                return (
                                    <circle
                                        key={`away-${index}`}
                                        cx={adjustedX * 120}
                                        cy={event.position.y * 70}
                                        r="1.2"
                                        fill="var(--away-team-color, #e53935)"
                                        stroke="white"
                                        strokeWidth="0.3"
                                        opacity="0.8"
                                    >
                                        <title>{`${event.eventType} - ${awayTeamName} - ${event.period}`}</title>
                                    </circle>
                                );
                            })}
                        </RugbyFieldSVG>
                    </div>
                    
                    {/* Zone Labels - Below the pitch */}
                    <div className="zone-labels-container">
                        {/* Red Zone Label */}
                        <div className="zone-label zone-label-red" style={{ width: redZoneWidth }}>
                            RED ZONE
                        </div>
                        
                        {/* Yellow Zone Label */}
                        <div className="zone-label zone-label-yellow" style={{ width: yellowZoneWidth }}>
                            YELLOW ZONE
                        </div>
                        
                        {/* Green Zone Label */}
                        <div className="zone-label zone-label-green" style={{ width: greenZoneWidth }}>
                            GREEN ZONE
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HeatmapView;